import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Ruler, Lightbulb, Volume2, Lock, Wifi, Truck, Paintbrush, Headset, ChevronDown, Box } from "lucide-react";
import { useNavigate } from "react-router-dom";
import podV1Image from "../assets/pod-v1.png";
import chairImage from "../assets/chair-photo.png";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const specs = [
  {
    icon: Ruler,
    title: "Dimensions",
    details: ["1m x 1m footprint", "2.3m height", "250kg - can be moved on wheels and supported to keep in place"],
  },
  {
    icon: Lightbulb,
    title: "Interior",
    details: [
      "comfortable chair with back support",
      "built-in desk",
      "adjustable lighting - warm white or bright white",
      "ventilation system",
      "bag and coat hook",
    ],
    expandable: true,
  },
  {
    icon: Volume2,
    title: "Soundproofing",
    details: ["30 dB noise reduction"],
  },
  {
    icon: Lock,
    title: "Locking & Access",
    details: [
      "locked by default from the outside",
      "numpad entry - access code provided upon booking",
      "unlocked from the inside at all times - zero risk of being stuck",
    ],
  },
  {
    icon: Wifi,
    title: "Connectivity",
    details: [
      "dedicated qwip Wi-Fi system - 100 Mbps+",
      "can also use venue Wi-Fi if preferred",
    ],
  },
  {
    icon: Headset,
    title: "Support",
    details: [
      "24/7 support via WhatsApp and phone",
      "report any issues, faults, or anything else at any time",
    ],
  },
  {
    icon: Truck,
    title: "Delivery & Installation",
    details: [
      "1-2 week lead time",
      "15 minute installation",
      "delivery, setup, and maintenance carried out by the qwip team",
    ],
  },
  {
    icon: Paintbrush,
    title: "Branding",
    details: [
      "wrapped in qwip branding by default",
      "can be wrapped in a custom style, colour, or your company's branding upon request",
    ],
  },
];

const TechSpecs = () => {
  const navigate = useNavigate();
  const [chairOpen, setChairOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-foreground/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/deck-view")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-sans"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 tablet:py-16 laptop:py-20">
        {/* Hero */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 tablet:mb-16"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans mb-4">
            Tech Specs
          </p>
          <h1 className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-6xl text-foreground leading-[0.9] tracking-tight mb-4">
            the qwip pod
          </h1>
          <p className="text-base tablet:text-lg text-muted-foreground font-sans max-w-md mx-auto">
            Everything you need to know about our soundproof pod.
          </p>
        </motion.div>

        {/* Pod Image */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex justify-center mb-14 tablet:mb-20"
        >
          <img
            src={podV1Image}
            alt="the qwip pod"
            className="w-full max-w-[240px] tablet:max-w-[300px] drop-shadow-2xl object-contain"
          />
        </motion.div>

        {/* Specs Grid */}
        <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4 tablet:gap-5">
          {specs.map((spec, i) => (
            <motion.div
              key={spec.title}
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.06 }}
              className="bg-foreground/[0.03] border border-foreground/5 rounded-2xl p-5 tablet:p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <spec.icon className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-funnel font-bold text-lg text-foreground">{spec.title}</h3>
              </div>
              <ul className="space-y-1.5">
                {spec.details.map((detail) => (
                  <li key={detail} className="text-sm text-muted-foreground font-sans leading-relaxed flex items-start gap-2">
                    <span className="text-primary/40 mt-1.5 flex-shrink-0">·</span>
                    {detail}
                  </li>
                ))}
              </ul>

              {/* Expandable chair section */}
              {spec.expandable && (
                <div className="mt-4">
                  <button
                    onClick={() => setChairOpen(!chairOpen)}
                    className="flex items-center gap-2 text-sm font-sans font-medium text-primary hover:text-primary/80 transition-colors bg-primary/5 hover:bg-primary/10 rounded-xl px-3 py-2"
                  >
                    <Box className="w-4 h-4" />
                    Images of chair
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${chairOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {chairOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <img
                          src={chairImage}
                          alt="qwip pod chair with back support"
                          className="w-2/3 max-w-[250px] rounded-xl mt-3"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="text-center mt-14 tablet:mt-20 pb-8"
        >
          <p className="text-sm text-muted-foreground font-sans">
            Have questions?{" "}
            <a href="mailto:hello@qwip.co.uk" className="text-foreground font-medium underline underline-offset-4">
              Get in touch
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TechSpecs;
